import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, Navigate, Link, useLocation, useNavigationType } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import NotificationBell from './components/NotificationBell';
import DeployUpdateBanner from './components/DeployUpdateBanner';
import { isPushSupported, isStandalone, registerPush } from './utils/pushManager';
import LoginPage from './components/LoginPage';
import Home from './pages/Home';
import Dashboard from './pages/Dashboard';
import AdsReport from './pages/AdsReport';
import Orders from './pages/Orders';
import Breaks from './pages/Breaks';
import BreakSpotReport from './pages/BreakSpotReport';
import Tasks from './pages/Tasks';
import Affiliates from './pages/Affiliates';
import Payouts from './pages/Payouts';
import AdminDashboard from './pages/AdminDashboard';
import CogsDiagnostic from './pages/CogsDiagnostic';
import Notifications from './pages/Notifications';
import Settings from './pages/Settings';
import Inventory from './pages/Inventory';
import InventoryCheckin from './pages/InventoryCheckin';
import InventoryReorder from './pages/InventoryReorder';
import Insights from './pages/Insights';
import CustomerData from './pages/CustomerData';
import LiveSessions from './pages/LiveSessions';
import Shipping from './pages/Shipping';
import BreakerCommission from './pages/BreakerCommission';
import BreakersReport from './pages/BreakersReport';
import LiveBanner from './pages/LiveBanner';
import AIChatIcon from './components/AIChatIcon';
import PullToRefresh from './components/PullToRefresh';
import ErrorBoundary from './components/ErrorBoundary';
import { DateRangeContext } from './contexts/DateRangeContext';
import LiveBreakSummary from './components/LiveBreakSummary';
import BreakDetailPanel from './components/BreakDetailPanel';

// A URL sits under a nav path when it is that path or something below it.
// Plain `startsWith` would also match /administrators from /admin.
const pathMatches = (pathname, base) => pathname === base || pathname.startsWith(base + '/');

// The active child is the LONGEST of a group's paths that the URL sits under.
// Equality alone stopped being enough once Admin's children nested inside each
// other: /admin/breaker-commission sits under Admin Dashboard's /admin as well,
// and the more specific one has to win. It also keeps Admin Dashboard lit while
// its own tabs push /admin/users, /admin/cogs and friends into the URL.
function activeChildPath(children, pathname) {
  const hits = children.filter(c => pathMatches(pathname, c.path));
  if (hits.length === 0) return null;
  return hits.reduce((best, c) => (c.path.length > best.path.length ? c : best)).path;
}

function ProtectedRoute({ children, roles }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" />;
  return children;
}

function App() {
  const { user, loading, logout, config, refreshUser } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [pushBanner, setPushBanner] = useState(false);
  const [liveSummaryBreakId, setLiveSummaryBreakId] = useState(null);
  const location = useLocation();
  const navigationType = useNavigationType();
  // Filled in by whichever DateRangeFilter is mounted; read when exporting.
  const activeDateRange = useRef(null);

  // On every page change, re-pull the current user so permission changes an
  // admin makes take effect on the user's next navigation (not only a full
  // refresh). Skipped on the very first load — the initial fetch handles that.
  // Keyed on pathname only: refreshUser() calls setUser, so depending on
  // `user` here would re-trigger on every refresh and loop forever.
  useEffect(() => {
    if (user) refreshUser();
  }, [location.pathname]);

  // Open every page at the top. React Router keeps the window's scroll
  // position across navigations, so picking a page from the sidebar while
  // part-way down a long list dropped you into the middle of the new one.
  //
  // Scoped to the section — the first path segment — so that opening a
  // detail view over a list (/breaks -> /breaks/123, which renders a panel
  // on top) doesn't scroll the list underneath it. POP is skipped so the
  // back button still restores where you were.
  const section = location.pathname.split('/')[1] || '';
  useEffect(() => {
    if (navigationType === 'POP') return;
    window.scrollTo(0, 0);
  }, [section]);

  // Lock the page behind the nav drawer. Without this the content keeps
  // scrolling under the open drawer on touch. Deliberately `overflow: hidden`
  // rather than the `position: fixed; top: -scrollY` trick — that loses the
  // scroll position and fights PullToRefresh's scrollTop reads.
  useEffect(() => {
    if (!sidebarOpen) return;
    if (!window.matchMedia('(max-width: 768px)').matches) return;
    document.body.classList.add('nav-open');
    return () => document.body.classList.remove('nav-open');
  }, [sidebarOpen]);

  useEffect(() => {
    if (!user || !isPushSupported() || !isStandalone()) return;
    // Show prompt only in standalone/PWA mode (mobile), not on desktop browser
    const dismissed = localStorage.getItem('push_prompt_dismissed');
    if (dismissed) {
      const dismissedAt = parseInt(dismissed, 10);
      if (Date.now() - dismissedAt < 7 * 24 * 60 * 60 * 1000) return; // 7 day cooldown
    }
    // Check if already subscribed
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistration('/sw.js').then(reg => {
        if (!reg) setPushBanner(true);
        else reg.pushManager.getSubscription().then(sub => { if (!sub) setPushBanner(true); });
      });
    }
  }, [user]);

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!user && location.pathname !== '/login') return <Navigate to="/login" />;
  if (location.pathname === '/login') return <LoginPage />;

  const navItems = [
    { path: '/', label: 'Dashboard', icon: 'home' },
    { path: '/financials', label: 'Financials', icon: 'chart', roles: ['admin', 'partner'] },
    { path: '/ads', label: 'Ads Report', icon: 'trending', roles: ['admin', 'partner'] },
    { path: '/orders', label: 'Orders', icon: 'receipt', roles: ['admin', 'partner', 'buyer'] },
    { path: '/breaks', label: 'Breaks', icon: 'calendar' },
    { path: '/inventory', label: 'Inventory', icon: 'box' },
    { path: '/tasks', label: 'Tasks', icon: 'check' },
    { path: '/affiliates', label: 'Affiliates', icon: 'users', roles: ['admin', 'partner'] },
    { path: '/payouts', label: 'Payouts', icon: 'dollar', roles: ['admin', 'partner'] },
    {
      path: '/reports/insights',
      label: 'Reports',
      icon: 'zap',
      roles: ['admin', 'partner'],
      children: [
        { path: '/reports/insights', label: 'Insights' },
        { path: '/reports/customer-data', label: 'Customer Data' },
        { path: '/reports/live-sessions', label: 'Live Sessions' },
        { path: '/reports/break-spots', label: 'Break Spots' },
      ],
    },
    { path: '/shipping', label: 'Shipping', icon: 'box', permission: 'manage_shipping' },
    { path: '/auction-grid-live-v2', label: 'Team Grid', icon: 'grid', newTab: true, roles: ['admin'] },
    { path: '/live-banner', label: 'Live Banner', icon: 'grid', roles: ['admin', 'partner'] },
    { path: '/notifications-config', label: 'Notifications', icon: 'bell', roles: ['admin'] },
    {
      path: '/admin',
      label: 'Admin',
      icon: 'settings',
      roles: ['admin'],
      children: [
        { path: '/admin', label: 'Admin Dashboard' },
        { path: '/admin/breaker-commission', label: 'Breaker Commission' },
        { path: '/admin/breakers-report', label: 'Breakers Report' },
      ],
    },
    { path: '/settings', label: 'Settings', icon: 'gear' },
  ];

  if (config?.atmobile?.enabled) {
    navItems.splice(-2, 0, {
      path: config.atmobile.url,
      label: 'ATmobile',
      icon: 'external',
      external: true
    });
  }

  const filteredNav = navItems.filter(item => (!item.roles || item.roles.includes(user.role))
    && (!item.permission || user.role === 'admin' || user.permissions?.[item.permission]));

  const handleATmobileClick = async (e, item) => {
    if (!item.external) return;
    e.preventDefault();
    try {
      const { data } = await (await import('./utils/api')).default.post('/api/auth/sso-token');
      const url = `${item.path}?sso_token=${data.ssoToken}`;
      const isMobile = window.innerWidth < 768;
      if (isMobile) { window.location.href = url; } else { window.open(url, '_blank'); }
    } catch { window.open(item.path, '_blank'); }
  };

  return (
    <ErrorBoundary>
    <DateRangeContext.Provider value={activeDateRange}>
    <PullToRefresh>
    <div className="app">
      <header className="topbar">
        <button className="menu-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}
          aria-label={sidebarOpen ? 'Close navigation menu' : 'Open navigation menu'}
          aria-expanded={sidebarOpen}>
          <span /><span /><span />
        </button>
        <Link to="/" className="logo" style={{ display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none', color: 'inherit', marginLeft: '8px' }}>
          <img src="/nucleus_white.png" alt="Nucleus" style={{ height: '28px' }} />
          <h1 className="logo-text">Nucleus</h1>
        </Link>
        {user?.role === 'admin' && (
          <LiveBreakSummary onSelectBreak={setLiveSummaryBreakId} />
        )}
        <div className="topbar-right">
          {config?.shops?.length > 1 && (
            <select className="shop-selector">
              {config.shops.map(s => <option key={s.shop_id} value={s.shop_id}>{s.shop_name}</option>)}
            </select>
          )}
          <NotificationBell />
          <div className="user-menu">
            <span className="user-name">{user.name}</span>
            <button onClick={logout} className="btn-link">Logout</button>
          </div>
        </div>
      </header>
      {user?.role === 'admin' && (
        <div className="mobile-break-bar">
          <LiveBreakSummary onSelectBreak={setLiveSummaryBreakId} />
        </div>
      )}
      {liveSummaryBreakId != null && (
        <BreakDetailPanel
          breakId={liveSummaryBreakId}
          canSeeFinancials={true}
          onClose={() => setLiveSummaryBreakId(null)}
        />
      )}

      <div className={`layout ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        <nav className={`sidebar ${sidebarOpen ? 'open' : ''} ${sidebarCollapsed ? 'collapsed' : ''}`} aria-label="Main navigation">
          {filteredNav.map(item => (
            item.external ? (
              <a key={item.path} href={item.path} className="nav-item"
                 onClick={(e) => handleATmobileClick(e, item)}>
                <span className={`nav-icon icon-${item.icon}`} />
                <span className="nav-label">{item.label}</span>
              </a>
            ) : item.newTab ? (
              <a key={item.path} href={item.path} className="nav-item"
                 target="_blank" rel="noopener noreferrer"
                 onClick={() => setSidebarOpen(false)}>
                <span className={`nav-icon icon-${item.icon}`} />
                <span className="nav-label">{item.label}</span>
              </a>
            ) : item.children ? (
              (() => {
                const activeChild = activeChildPath(item.children, location.pathname);
                const childActive = !!activeChild;
                return (
                  <div key={item.path} className={`nav-group${childActive ? ' nav-group-active' : ''}`}>
                    <Link to={item.path}
                          className={`nav-item ${childActive ? 'active' : ''}`}
                          onClick={() => setSidebarOpen(false)}>
                      <span className={`nav-icon icon-${item.icon}`} />
                      <span className="nav-label">{item.label}</span>
                    </Link>
                    <div className="nav-children">
                      {item.children.map(child => (
                        <Link key={child.path} to={child.path}
                              className={`nav-subitem ${child.path === activeChild ? 'active' : ''}`}
                              onClick={() => setSidebarOpen(false)}>
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                );
              })()
            ) : (
              <Link key={item.path} to={item.path}
                    className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}
                    onClick={() => setSidebarOpen(false)}>
                <span className={`nav-icon icon-${item.icon}`} />
                <span className="nav-label">{item.label}</span>
              </Link>
            )
          ))}
          {/* Host for the deploy status pill, which DeployUpdateBanner
              portals into so it sits at the foot of the menu rather than
              floating over the page. */}
          <div className="sidebar-status" id="sidebar-status" />
          <button className="sidebar-collapse-btn" onClick={() => setSidebarCollapsed(!sidebarCollapsed)}>
            {sidebarCollapsed ? '▶' : '◀'} {!sidebarCollapsed && <span className="nav-label">Collapse</span>}
          </button>
        </nav>

        {sidebarOpen && (
          <div className="sidebar-scrim" onClick={() => setSidebarOpen(false)} aria-hidden="true" />
        )}

        {/* Tapping the page closes the drawer. The DOM check matters: React
            portals bubble events through the React tree rather than the DOM
            one, so the deploy pill — rendered into the sidebar but owned by a
            component that lives in here — was firing this and closing the
            menu out from under itself. Portalled content is not a DOM
            descendant of <main>, so contains() rejects it. */}
        <main
          className="content"
          onClick={(e) => {
            if (sidebarOpen && e.currentTarget.contains(e.target)) setSidebarOpen(false);
          }}
        >
          {/* Banners live inside the scrollable content area. Anything placed above
              the fixed topbar is hidden behind it while still pushing the whole
              layout down, which reads as dead space under the header. */}
          <DeployUpdateBanner />
          {config?.usingSeedData && (
            <div className="seed-banner">Running on seed data — no live API connections</div>
          )}
          {pushBanner && (
            <div className="seed-banner" style={{ background: '#2d3748', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', padding: '8px 16px' }}>
              <span>Enable push notifications to get alerts when the app is closed</span>
              <button className="btn btn-primary btn-sm" onClick={async () => {
                try { await registerPush(); } catch (e) { console.log('Push registration failed:', e); }
                setPushBanner(false);
                localStorage.setItem('push_prompt_dismissed', String(Date.now()));
              }}>Allow</button>
              <button className="btn btn-secondary btn-sm" onClick={() => {
                setPushBanner(false);
                localStorage.setItem('push_prompt_dismissed', String(Date.now()));
              }}>Later</button>
            </div>
          )}
          <Routes>
            <Route path="/" element={<ProtectedRoute><Home /></ProtectedRoute>} />
            <Route path="/financials" element={<ProtectedRoute roles={['admin','partner']}><Dashboard /></ProtectedRoute>} />
            <Route path="/ads" element={<ProtectedRoute roles={['admin','partner']}><AdsReport /></ProtectedRoute>} />
            <Route path="/orders" element={<ProtectedRoute roles={['admin','partner','buyer']}><Orders /></ProtectedRoute>} />
            <Route path="/orders/:orderId" element={<ProtectedRoute roles={['admin','partner','buyer']}><Orders /></ProtectedRoute>} />
            <Route path="/breaks" element={<ProtectedRoute><Breaks /></ProtectedRoute>} />
            <Route path="/breaks/:breakId" element={<ProtectedRoute><Breaks /></ProtectedRoute>} />
            <Route path="/breaks/:breakId/spots" element={<ProtectedRoute><Breaks /></ProtectedRoute>} />
            <Route path="/inventory" element={<ProtectedRoute><Inventory /></ProtectedRoute>} />
            <Route path="/inventory/checkin/:id" element={<ProtectedRoute><InventoryCheckin /></ProtectedRoute>} />
            <Route path="/inventory/reorder" element={<ProtectedRoute><InventoryReorder /></ProtectedRoute>} />
            <Route path="/tasks" element={<ProtectedRoute><Tasks /></ProtectedRoute>} />
            <Route path="/tasks/:taskId" element={<ProtectedRoute><Tasks /></ProtectedRoute>} />
            <Route path="/affiliates" element={<ProtectedRoute roles={['admin','partner']}><Affiliates /></ProtectedRoute>} />
            <Route path="/payouts" element={<ProtectedRoute roles={['admin','partner']}><Payouts /></ProtectedRoute>} />
            <Route path="/reports" element={<Navigate to="/reports/insights" replace />} />
            <Route path="/reports/insights" element={<ProtectedRoute roles={['admin','partner']}><Insights /></ProtectedRoute>} />
            <Route path="/reports/customer-data" element={<ProtectedRoute roles={['admin','partner']}><CustomerData /></ProtectedRoute>} />
            <Route path="/reports/live-sessions" element={<ProtectedRoute roles={['admin','partner']}><LiveSessions /></ProtectedRoute>} />
            <Route path="/reports/break-spots" element={<ProtectedRoute roles={['admin','partner']}><BreakSpotReport /></ProtectedRoute>} />
            <Route path="/shipping" element={<ProtectedRoute><Shipping /></ProtectedRoute>} />
            <Route path="/vip" element={<Navigate to="/reports/customer-data" replace />} />
            <Route path="/reports/live-data" element={<Navigate to="/reports/customer-data" replace />} />
            <Route path="/insights" element={<Navigate to="/reports/insights" replace />} />
            <Route path="/breaker-commission" element={<Navigate to="/admin/breaker-commission" replace />} />
            <Route path="/live-banner" element={<ProtectedRoute roles={['admin','partner']}><LiveBanner /></ProtectedRoute>} />
            <Route path="/notifications-config" element={<ProtectedRoute roles={['admin']}><Notifications /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute roles={['admin']}><AdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/breaker-commission" element={<ProtectedRoute roles={['admin']}><BreakerCommission /></ProtectedRoute>} />
            <Route path="/admin/breakers-report" element={<ProtectedRoute roles={['admin']}><BreakersReport /></ProtectedRoute>} />
            <Route path="/admin/cogs-diagnostic" element={<ProtectedRoute roles={['admin']}><CogsDiagnostic /></ProtectedRoute>} />
            <Route path="/admin/sku-aliases" element={<Navigate to="/admin/cogs-diagnostic" replace />} />
            <Route path="/admin/:tab" element={<ProtectedRoute roles={['admin']}><AdminDashboard /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </main>
      </div>
      <AIChatIcon />
    </div>
    </PullToRefresh>
    </DateRangeContext.Provider>
    </ErrorBoundary>
  );
}

export default App;
